import { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../utils/api-client';

// Types
interface CategoryUsage {
  categoryId: string;
  categoryName: string;
  totalAmount: number;
  usageCount: number;
  hostCount: number;
  averageAmount: number;
  percentageOfTotal: number;
  topHosts: Array<{
    hostId: string;
    hostName: string;
    amount: number;
    count: number;
  }>;
}

interface CategoryTrend {
  categoryId: string;
  categoryName: string;
  monthlyUsage: Array<{
    month: string;
    amount: number;
    count: number;
  }>;
  trend: 'increasing' | 'decreasing' | 'stable';
}

interface CategoryUsageReport {
  totalCategories: number;
  totalUsage: number;
  categoryStats: CategoryUsage[];
  mostPopularCategories: CategoryUsage[];
  leastUsedCategories: CategoryUsage[];
  categoryTrends: CategoryTrend[];
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

interface UseCategoryUsageReportsOptions {
  dateRange?: {
    startDate: Date;
    endDate: Date;
  };
  autoRefresh?: boolean;
  refreshInterval?: number;
}

interface UseCategoryUsageReportsReturn {
  data: CategoryUsageReport | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  exportData: (format: 'csv' | 'excel') => Promise<void>;
}

/**
 * Custom hook for fetching category usage reports
 */
export const useCategoryUsageReports = (
  options: UseCategoryUsageReportsOptions = {}
): UseCategoryUsageReportsReturn => {
  const {
    dateRange,
    autoRefresh = false,
    refreshInterval = 300000 // 5 minutes
  } = options;

  const [data, setData] = useState<CategoryUsageReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch category usage data
  const fetchCategoryUsageData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Build query parameters
      const params = new URLSearchParams();
      
      if (dateRange) {
        params.append('startDate', dateRange.startDate.toISOString());
        params.append('endDate', dateRange.endDate.toISOString());
      }

      const response = await apiClient.get<CategoryUsageReport>(`/admin/financial/category-usage?${params.toString()}`);
      
      if (response.success) {
        setData(response.data);
      } else {
        throw new Error(response.message || 'Failed to fetch category usage data');
      }
    } catch (err) {
      console.error('Error fetching category usage data:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch category usage data');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // Export category usage data
  const exportData = useCallback(async (format: 'csv' | 'excel') => {
    try {
      setError(null);

      const response = await apiClient.post<{ exportId: string; downloadUrl: string }>('/admin/financial/system-export', {
        format,
        includeDetails: true,
        dateRange: dateRange ? {
          startDate: dateRange.startDate.toISOString(),
          endDate: dateRange.endDate.toISOString()
        } : undefined,
        reportType: 'category-usage'
      });

      if (response.success) {
        // In a real implementation, this would handle the download
        // For now, we'll just log the export result
        console.log('Export initiated:', response.data);
        
        // You could trigger a download here or show a notification
        // that the export is being processed
      } else {
        throw new Error(response.message || 'Failed to export category usage data');
      }
    } catch (err) {
      console.error('Error exporting category usage data:', err);
      setError(err instanceof Error ? err.message : 'Failed to export category usage data');
    }
  }, [dateRange]);

  // Refresh function
  const refresh = useCallback(async () => {
    await fetchCategoryUsageData();
  }, [fetchCategoryUsageData]);

  // Initial data fetch
  useEffect(() => {
    fetchCategoryUsageData();
  }, [fetchCategoryUsageData]);

  // Auto-refresh setup
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchCategoryUsageData();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, fetchCategoryUsageData]);

  return {
    data,
    loading,
    error,
    refresh,
    exportData
  };
};

export default useCategoryUsageReports;